import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import {
  FormGroup,
  Button,
  ControlGroup,
  Intent
} from "@blueprintjs/core";
import FormDrawer from "components/FormDrawer";
import { FormSection, FieldError, ClientFlags, GrowingTextArea } from "components/FormBits";
import ClientAutocomplete from "components/ClientAutocomplete";
import AddClientModal from "components/Client/AddClientModal";
import AddLocationModal from "components/Client/AddLocationModal";
import TechnicianPicker from "components/TechnicianPicker";
import LocationAutocomplete from "components/LocationAutocomplete";
import JobPicker from "components/JobPicker";
import Toast from "helpers/toaster";
import { Api } from "services/Api";
import { observable, toJS } from 'mobx';

// New invoice, in the same shape as New invoice and New work order:
// Customer (+ location) → Details (job, PO, description) → More options (created by).
@inject("viewStore", "invoiceStore")
@observer
class AddInvoiceModal extends Component {
  @observable selectedOptions = null;
  @observable job_detals = null;
  // Client handed in by the opener (e.g. the client page), shown as the
  // autocomplete's selection until the user clears or changes it.
  @observable preselectedClient = null;
  // The client picked in this form (for its do-not-service / alert notes).
  @observable chosenClient = null;
  @observable errors = {};
  @observable isOpenAddClientDialog = false;
  @observable isOpenAddLocationDialog = false;
  locationLookup = 0;

  componentDidMount() {
    const {
      viewStore: {
        currentUser
      },
      invoiceStore: {
        newInvoice: {
          object
        }
      },
      job
    } = this.props;
    object.technician_id = [currentUser.id]
    if (!object.po_number && job && job.attributes && job.attributes.po_number) {
      object.po_number = job.attributes.po_number;
    }
    if (this.props.isOpen) {
      this.onOpen();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.onOpen();
    }
  }

  onOpen() {
    const { object } = this.props.invoiceStore.newInvoice;
    const { currentUser } = this.props.viewStore;
    this.errors = {};
    // "Created by" defaults to whoever is creating it.
    if (!this.hasTechnician() && currentUser && currentUser.id) {
      object.technician_id = [currentUser.id];
    }
    // A client remembered from before (e.g. made with "New client") that the
    // form no longer has, because that record was saved: don't show it.
    if (this.preselectedClient && this.preselectedClient.id !== object.client_id) {
      this.preselectedClient = null;
    }
    if (this.chosenClient && this.chosenClient.id !== object.client_id) {
      this.chosenClient = null;
    }
    this.preselectClient();
    if (object.client_id && !object.location_id) {
      this.pickOnlyLocation(object.client_id);
    }
  }

  // Picks `client` exactly as if it had been chosen in the autocomplete.
  preselectClient() {
    const { client } = this.props;
    if (!client) {
      return;
    }
    let { object } = this.props.invoiceStore.newInvoice;
    if (object.client_id !== client.id) {
      // A location left over from another client doesn't belong to this one.
      this.onLocationClear();
    }
    this.onClientSelect(client);
    this.preselectedClient = client;
  }

  onClientSelect(client) {
    let { object } = this.props.invoiceStore.newInvoice;
    this.preselectedClient = null;
    this.chosenClient = client || null;
    if (object && client) {
      if (object.client_id && object.client_id !== client.id) {
        // Another client's location doesn't belong to this one.
        this.onLocationClear();
      }
      object.client_id = client.id;
      this.errors = { ...this.errors, client: null };
      if (!object.location_id) {
        this.pickOnlyLocation(client.id);
      }
    } else {
      this.onClientClear();
      this.onLocationClear();
    }
  }

  // When the client has exactly one location, choose it (only if none is chosen yet).
  pickOnlyLocation(clientId) {
    const lookup = ++this.locationLookup;
    Api.post('/locations/search', {
      search: { term: '', per_page: 2, page: 1, order: { name: 'asc' }, where: { client_id: clientId } }
    })
      .then((response) => {
        const { object } = this.props.invoiceStore.newInvoice;
        const locations = (response && response.data && response.data.data) || [];
        if (lookup !== this.locationLookup || object.client_id !== clientId || object.location_id) {
          return;
        }
        if (locations.length === 1) {
          object.location_id = locations[0].id;
          object.location = locations[0];
        }
      })
      .catch(() => {});
  }

  hasTechnician() {
    const { technician_id } = this.props.invoiceStore.newInvoice.object;
    if (Array.isArray(technician_id)) {
      return technician_id.filter(Boolean).length > 0;
    }
    return Boolean(technician_id);
  }

  validate() {
    const { object } = this.props.invoiceStore.newInvoice;
    const errors = {};
    if (object.client_id === '' || object.client_id === null || object.client_id === undefined) {
      errors.client = 'Choose the customer this invoice is for.';
    }
    if (!this.hasTechnician()) {
      errors.technician = 'Choose who is creating this invoice.';
    }
    return errors;
  }

  focusFirstError(errors) {
    const target = errors.client ? 'invoice-customer' : errors.technician ? 'invoice-created-by' : null;
    if (!target) {
      return;
    }
    setTimeout(() => {
      const element = document.getElementById(target);
      if (!element) {
        return;
      }
      element.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const focusable = element.matches('input, button') ? element : element.querySelector('input, button');
      if (focusable) {
        focusable.focus();
      }
    }, 50);
  }

  createInvoice() {
    const errors = this.validate();
    this.errors = errors;
    if (Object.keys(errors).length > 0) {
      this.focusFirstError(errors);
      return;
    }

    this.props.invoiceStore
      .createEmpty()
      .then(response => {
        this.props.onClose();
        Toast.show({
          icon: "thumbs-up",
          message: "Invoice created successfully!",
          intent: Intent.SUCCESS
        });
      })
      .catch(error => {
        Toast.show({
          icon: "warning-sign",
          message: "Unable to create invoice!",
          intent: Intent.DANGER
        });
      });
  }

  handleCloseInvoiceDailog() {
    this.errors = {};
    if (this.props.client) {
      // The store's form is shared with the top bar's "+ New" dialog; don't
      // leave this page's client behind in it.
      this.preselectedClient = null;
      this.chosenClient = null;
      this.onClientClear();
      this.onLocationClear();
    }
    this.props.onClose();
  }

  onClientClear = () => {
    let { object } = this.props.invoiceStore.newInvoice;
    object.client_id = null;
    object.selectedClient = '';
  };

  onTechnicianChange(technicians) {
    let {
      props: {
        invoiceStore: {
          newInvoice: { object }
        }
      }
    } = this;
    let selectedValues = technicians.map(status => status.value);
    object.technician_id = selectedValues.length > 0 ? [selectedValues[0]] : '';
    if (selectedValues.length > 0) {
      this.errors = { ...this.errors, technician: null };
    }
  }

  onJobSelect(selectedOptions, allOptions) {
    let {
      props: {
        invoiceStore: {
          newInvoice: { object }
        }
      }
    } = this;
    this.selectedOptions = selectedOptions;
    object.selectedJob = [selectedOptions[0]];
    object.scheduled_event_id = selectedOptions[0].value;
  }

  // A client made with "New client": pick it, as if chosen in the search.
  onSuccessAddClient(client) {
    this.isOpenAddClientDialog = false;
    this.onClientSelect(client);
    this.preselectedClient = client;
  }

  // A location made with "New location": pick it for this customer.
  onSuccessAddLocation(location) {
    const { object } = this.props.invoiceStore.newInvoice;
    this.isOpenAddLocationDialog = false;
    object.location_id = location.id;
    object.location = location;
  }

  onLocationClear = () => {
    let {
      props: {
        invoiceStore: {
          newInvoice: { object }
        }
      }
    } = this;
    object.location_id = null;
    object.location = null;
  };

  // The client to show in the Customer box: the one remembered for this form
  // (passed in, or made with "New client") only while the form still has it.
  // Once a saved record resets the form, the next open must show nothing.
  shownClient(object) {
    const client = this.preselectedClient;
    if (client && client.id === object.client_id) {
      return client;
    }
    return object.selectedClient;
  }

  clientNotes(object) {
    const client = this.chosenClient || this.preselectedClient || object.selectedClient;
    return <ClientFlags client={client} />;
  }

  render() {
    let { object } = this.props.invoiceStore.newInvoice;
    let { edit, job } = this.props;
    let jobPoNumber = job && job.attributes && job.attributes.po_number;
    const busy = Boolean(this.props.invoiceStore.newInvoice.isLoading);
    const close = this.handleCloseInvoiceDailog.bind(this);
    const errors = this.errors;
    return (
      <React.Fragment>
        <FormDrawer
          size="min(760px, 100vw)"
          isOpen={this.props.isOpen}
          title="New invoice"
          icon="dollar"
          onClose={close}
          busy={busy}
          footer={
            <>
              <Button onClick={close} disabled={busy}>
                Cancel
              </Button>
              <Button
                intent={Intent.PRIMARY}
                loading={busy}
                onClick={this.createInvoice.bind(this)}
              >
                Create invoice
              </Button>
            </>
          }
        >
          <FormSection title="Customer" hint="Who you're billing and where the work was done.">
            <FormGroup
              className={errors.client ? 'has-error' : undefined}
              label="Customer"
              labelFor="invoice-customer"
              labelInfo={<span className="fb-required">*</span>}
            >
              <ControlGroup fill={true} vertical={false} className="fb-picker">
                <ClientAutocomplete
                  showDetails={true}
                  inputProps={{
                    id: "invoice-customer",
                    placeholder: "Search by name, phone number or email",
                    'aria-invalid': Boolean(errors.client),
                    'aria-describedby': errors.client ? 'invoice-customer-error' : undefined
                  }}
                  value={this.shownClient(object)}
                  onClientSelect={this.onClientSelect.bind(this)}
                />
                <Button
                  icon="add"
                  text="New client"
                  onClick={() => { this.isOpenAddClientDialog = true; }}
                  className="fb-add-button"
                />
              </ControlGroup>
              <FieldError id="invoice-customer-error">{errors.client}</FieldError>
              {object.client_id && this.clientNotes(object)}
            </FormGroup>
            {object.client_id && (
              <FormGroup
                label="Location"
                labelFor="invoice-location"
                helperText={object.location_id ? undefined : "Picked for you when the customer has only one."}
              >
                <ControlGroup fill={true} vertical={false} className="fb-picker">
                  <LocationAutocomplete
                    key={object.client_id}
                    scope={{ client_id: object.client_id }}
                    inputProps={{
                      id: "invoice-location",
                      fill: true,
                      placeholder: "Search this customer's locations"
                    }}
                    onLocationClear={this.onLocationClear.bind(this)}
                    value={object.location}
                    onLocationSelect={location => {
                      if (object && location) {
                        object.location_id = location.id;
                        object.location = location;
                      } else {
                        this.onLocationClear();
                      }
                    }}
                  />
                  <Button
                    icon="add"
                    text="New location"
                    onClick={() => { this.isOpenAddLocationDialog = true; }}
                    className="fb-add-button"
                  />
                </ControlGroup>
              </FormGroup>
            )}
          </FormSection>

          <FormSection title="Details" hint="You'll add the line items on the invoice itself.">
            <div className="fb-grid">
              {edit === true && (
                <FormGroup label="Job">
                  <JobPicker
                    selectedOptions={toJS(object.selectedJob)}
                    onChange={this.onJobSelect.bind(this)}
                    maximumSelections={1}
                  />
                </FormGroup>
              )}
              <FormGroup label="PO number" labelFor="invoice-po-number">
                <input
                  id="invoice-po-number"
                  type="text"
                  className="bp3-input bp3-fill"
                  placeholder="e.g. PO-20417"
                  value={object.po_number || jobPoNumber || ''}
                  onChange={event => {
                    object.po_number = event.target.value || null;
                  }}
                />
              </FormGroup>
              <FormGroup
                className={errors.technician ? 'has-error' : undefined}
                label="Created by"
                labelInfo={<span className="fb-required">*</span>}
                helperText={errors.technician ? undefined : "You, unless you're making it for someone else."}
              >
                <div id="invoice-created-by">
                  <TechnicianPicker
                    selectedOptions={object.technician_id || ""}
                    onChange={this.onTechnicianChange.bind(this)}
                    includeAdmins={true}
                    showCreatedBy={true}
                    showName={true}
                    maximumSelections={1}
                  />
                </div>
                <FieldError>{errors.technician}</FieldError>
              </FormGroup>
            </div>

            <FormGroup label="Description" labelFor="invoice-description">
              <GrowingTextArea
                id="invoice-description"
                className="bp3-fill"
                placeholder="e.g. Replace 3-ton AC condenser and thermostat"
                value={object.description || ''}
                onChange={event => {
                  object.description = event.target.value;
                }}
              />
            </FormGroup>
          </FormSection>
        </FormDrawer>
        {/* Outside the panel, so Escape in one of these closes only it. */}
        <AddClientModal
          isOpen={this.isOpenAddClientDialog}
          onClose={() => { this.isOpenAddClientDialog = false; }}
          onSuccess={this.onSuccessAddClient.bind(this)}
        />
        <AddLocationModal
          isOpen={this.isOpenAddLocationDialog}
          client_id={object.client_id}
          onClose={() => { this.isOpenAddLocationDialog = false; }}
          onSuccess={this.onSuccessAddLocation.bind(this)}
        />
      </React.Fragment>
    );
  }
}

export default AddInvoiceModal;
